import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Building2, ChevronRight, Dumbbell, Home, Search, Users } from 'lucide-react';
import { Link } from 'react-router-dom';

const signupPaths = [
  {
    id: 'find-gym',
    title: 'Find My Gym',
    description: 'Already a member? Search for your gym and join their ErgShoot community.',
    icon: Search,
    href: '/find-gym',
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
  },
  {
    id: 'start-gym',
    title: 'Start a Gym',
    description:
      'Gym owner? Apply to become an ErgShoot affiliate and access programming for your members.',
    icon: Building2,
    href: '/apply/affiliate',
    color: 'text-primary',
    bgColor: 'bg-primary/10',
    badge: '£67.15/mo',
  },
  {
    id: 'train-home',
    title: 'Train at Home',
    description: 'Individual athlete? Get access to ErgShoot programming for personal training.',
    icon: Home,
    href: '/signup/individual',
    color: 'text-green-500',
    bgColor: 'bg-green-500/10',
    badge: '$6.75/mo',
  },
  {
    id: 'become-agent',
    title: 'Become an Agent',
    description: 'Interested in becoming a territory partner? Contact our sales team.',
    icon: Users,
    href: '/apply/agent',
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-transparent pointer-events-none" />

        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="text-center max-w-3xl mx-auto">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <img src="/ergshoot-logo.png" alt="ErgShoot" className="h-20 w-auto" />
            </div>

            {/* Title */}
            <h1 className="text-4xl md:text-5xl font-bold text-gradient-gold mb-4">ErgShoot</h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-2">
              The Ultimate Rowing & Shooting Workout Platform
            </p>
            <p className="text-muted-foreground mb-8">
              Join thousands of athletes and gyms worldwide
            </p>

            {/* Already have account */}
            <div className="mb-12">
              <Button variant="outline" size="lg" asChild>
                <Link to="/login">
                  Already have an account? Sign In
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Signup Paths */}
      <div className="container mx-auto px-4 pb-16">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-foreground mb-2">Get Started</h2>
          <p className="text-muted-foreground">Choose how you want to use ErgShoot</p>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 max-w-6xl mx-auto">
          {signupPaths.map((path, index) => {
            const Icon = path.icon;
            return (
              <Link to={path.href} key={path.id}>
                <Card
                  className="h-full hover:border-primary/50 transition-all cursor-pointer animate-fade-in group"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className={`rounded-lg p-2 ${path.bgColor}`}>
                        <Icon className={`h-6 w-6 ${path.color}`} />
                      </div>
                      {path.badge && (
                        <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded-full">
                          {path.badge}
                        </span>
                      )}
                    </div>
                    <CardTitle className="text-lg group-hover:text-primary transition-colors">
                      {path.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm">{path.description}</CardDescription>
                    <div className="mt-4 flex items-center text-sm text-primary font-medium">
                      Get Started
                      <ChevronRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-muted/30 border-t border-border">
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold text-foreground mb-2">Why ErgShoot?</h2>
            <p className="text-muted-foreground">
              Everything you need for rowing and shooting fitness
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-3 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="rounded-full bg-primary/10 p-3">
                  <Dumbbell className="h-6 w-6 text-primary" />
                </div>
              </div>
              <h3 className="font-semibold mb-2">Expert Programming</h3>
              <p className="text-sm text-muted-foreground">
                Daily workouts designed by rowing and fitness experts
              </p>
            </div>

            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="rounded-full bg-primary/10 p-3">
                  <Users className="h-6 w-6 text-primary" />
                </div>
              </div>
              <h3 className="font-semibold mb-2">Community Leaderboards</h3>
              <p className="text-sm text-muted-foreground">
                Compete with athletes worldwide and track your progress
              </p>
            </div>

            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="rounded-full bg-primary/10 p-3">
                  <Building2 className="h-6 w-6 text-primary" />
                </div>
              </div>
              <h3 className="font-semibold mb-2">Gym Integration</h3>
              <p className="text-sm text-muted-foreground">
                TV display mode and member management for affiliates
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} ErgShoot. All rights reserved.</p>
          <div className="mt-2 space-x-4">
            <a href="mailto:hello@ergshoot.com" className="hover:text-primary transition-colors">
              Contact Support
            </a>
            <Link to="/privacy" className="hover:text-primary transition-colors">
              Privacy Policy
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
